/**
 * InstructionsEditor Component
 *
 * Allows users to view and edit step-specific instructions.
 * Handles loading, saving, and empty states gracefully.
 */

import React, {useState, useEffect, useCallback} from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {apiService} from '../services/api';

interface InstructionsEditorProps {
  trialId: string;
  sessionId: string;
  platform: 'app' | 'web';
  step: 'requirements' | 'plan' | 'execution';
  title?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const InstructionsEditor: React.FC<InstructionsEditorProps> = ({
  trialId,
  sessionId,
  platform,
  step,
  title,
  placeholder = 'Add instructions for this step...',
  disabled = false,
}) => {
  const [instructions, setInstructions] = useState<string>('');
  const [originalInstructions, setOriginalInstructions] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const loadInstructions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await apiService.getSessionInstructions(
        trialId,
        sessionId,
        platform,
        step
      );
      
      const instructionsText = data?.instructions || '';
      setInstructions(instructionsText);
      setOriginalInstructions(instructionsText);
    } catch (err: any) {
      // Gracefully handle errors - don't block the UI
      console.error('Failed to load instructions:', err);
      setInstructions('');
      setOriginalInstructions('');
    } finally {
      setIsLoading(false);
    }
  }, [trialId, sessionId, platform, step]);

  // Load instructions on mount or when dependencies change
  useEffect(() => {
    loadInstructions();
  }, [loadInstructions]);

  const handleEdit = () => {
    setIsEditing(true);
    setSaveSuccess(false);
  };

  const handleCancel = () => {
    setInstructions(originalInstructions);
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const result = await apiService.saveSessionInstructions(
        trialId,
        sessionId,
        platform,
        step,
        instructions
      );

      if (result.success) {
        setOriginalInstructions(instructions);
        setIsEditing(false);
        setSaveSuccess(true);
        
        // Hide success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(result.message || 'Failed to save instructions');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save instructions');
    } finally {
      setIsSaving(false);
    }
  };

  const displayTitle = title || `${step.charAt(0).toUpperCase() + step.slice(1)} Instructions`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: (theme) => 
          theme.palette.mode === 'dark' 
            ? 'background.paper' 
            : 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: isExpanded ? 2 : 0,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle2" fontWeight={600}>
            {displayTitle}
          </Typography>
        </Box>

        {isExpanded && !isEditing && !disabled && (
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{textTransform: 'none'}}
          >
            {instructions ? 'Edit' : 'Add'}
          </Button>
        )}
      </Box>

      {/* Content */}
      <Collapse in={isExpanded}>
        {isLoading ? (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Success Alert */}
            {saveSuccess && (
              <Alert severity="success" sx={{mb: 2}}>
                Instructions saved successfully
              </Alert>
            )}

            {/* Instructions Text Area / Display */}
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder={placeholder}
                  variant="outlined"
                  disabled={isSaving}
                  sx={{mb: 2}}
                />
                <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
                  <Button
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={isSaving}
                    sx={{textTransform: 'none'}}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={isSaving}
                    sx={{textTransform: 'none'}}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  p: 2,
                  bgcolor: (theme) => 
                    theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)' 
                      : 'grey.50',
                  borderRadius: 1,
                  minHeight: 60,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                {instructions ? (
                  <Typography variant="body2" color="text.primary">
                    {instructions}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No instructions added yet
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Collapse>
    </Paper>
  );
};
